import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router";

import { signOut, useSession } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";
import { AppSidebar } from "@/components/app-sidebar";
import { Button } from "@runcheetah/ui";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@runcheetah/ui";
import { getAdminRouteMeta } from "./admin-route-meta";

const SESSION_TIMEOUT_MS = 6_000;

const handleSignOut = () => {
  void signOut().finally(() => window.location.assign("/login"));
};

function StuckScreen() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4">
      <p className="text-sm text-muted-foreground">
        Could not reach the server. Check that the API is running.
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          Retry
        </Button>
        <Button variant="ghost" size="sm" onClick={handleSignOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

function ForbiddenScreen() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4">
      <p className="text-sm font-medium">Access denied</p>
      <p className="text-sm text-muted-foreground">
        Your account is not authorised to access the admin panel.
      </p>
      <Button variant="outline" size="sm" onClick={handleSignOut}>
        Sign out
      </Button>
    </div>
  );
}

export function AdminLayout() {
  const { data: session, isPending, error } = useSession();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const adminCheck = trpc.admin.check.useQuery(undefined, {
    enabled: !!session?.user,
    retry: false,
  });

  useEffect(() => {
    if (isPending) {
      timeoutRef.current = setTimeout(() => setTimedOut(true), SESSION_TIMEOUT_MS);
    } else {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setTimedOut(false);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isPending]);

  if (isPending && timedOut) {
    return <StuckScreen />;
  }

  if (isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading…</div>
      </div>
    );
  }

  if (error) {
    return <StuckScreen />;
  }

  if (!session?.user) {
    return <Navigate to="/login" replace />;
  }

  if (adminCheck.isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <div className="text-muted-foreground text-sm">Verifying access…</div>
      </div>
    );
  }

  if (adminCheck.isError) {
    return <ForbiddenScreen />;
  }

  const routeMeta = getAdminRouteMeta(location.pathname);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="min-w-0">
        {/* Top bar */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <div className="min-w-0">
            <h1 className="text-sm font-medium leading-5">{routeMeta.title}</h1>
            <nav
              aria-label="Breadcrumb"
              className="hidden min-w-0 items-center gap-1 text-xs text-muted-foreground md:flex"
            >
              {routeMeta.breadcrumbs.map((crumb, index) => {
                const isLast = index === routeMeta.breadcrumbs.length - 1;
                return (
                  <span key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1">
                    {index > 0 ? <span aria-hidden="true">/</span> : null}
                    {crumb.href && !isLast ? (
                      <Link
                        to={crumb.href}
                        className="truncate hover:text-foreground hover:underline"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="truncate">{crumb.label}</span>
                    )}
                  </span>
                );
              })}
            </nav>
          </div>
        </header>

        {/* Page content */}
        <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-x-hidden p-6">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
